import React, { useState, useContext, useEffect, useRef } from "react";
import CrisisModal from "./CrisisModal";
import DotMovement from "./DotMovement";
import DotContext from "../contexts/DotContext";
import { SessionThemeWrapper } from "../contexts/SessionThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import MoodTracker from "./MoodTracker";
import ReadinessCheck from "./ReadinessCheck";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import DynamicTitle from "./DynamicTitle";
import LoadingSkeleton from "./LoadingSkeleton";
import { useAuthValidation } from "../hooks/useAuthValidation";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../utils/posthog";
import { soundFileToOption } from "../utils/soundOptions";

function UnguidedSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const intakeData = location.state?.intakeData ?? null;
  const { user } = useAuth();
  // Unguided sessions are part of the free tier — only require a logged-in user.
  const isValidated = useAuthValidation({ requireSubscription: false });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [showDotMovement, setShowDotMovement] = useState(false);
  const [showReadinessCheck, setShowReadinessCheck] = useState(true);
  const [moodModalVisible, setMoodModalVisible] = useState(false);
  const [sessionId, setSessionId] = useState();
  const sessionIdRef = useRef(null);
  const {
    bgColor,
    dotColor,
    dotGlow,
    dotSize,
    dotSpeed,
    dotBrightness,
    isPaused,
    setIsPaused,
    setSidebarVisible,
    sidebarVisible,
    soundFile,
    mute,
    volume,
    sessionCuesEnabled,
  } = useContext(DotContext);
  const [preSessionMood, setPreSessionMood] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (!isValidated && !user) {
      navigate("/login", { replace: true, state: { from: "/sessions/unguided" } });
    }
  }, [isValidated, user, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.user_id);
    }
  }, []);

  // Keep ref in sync so cleanup callbacks can access the latest sessionId
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Abandon session on tab close or component unmount
  useEffect(() => {
    const abandonSession = () => {
      const id = sessionIdRef.current;
      const token = localStorage.getItem("token");
      if (!id || !token) return;

      const url = `/api/sessions/${id}/abandon`;

      try {
        fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({}),
          keepalive: true,
        });
      } catch {
        navigator.sendBeacon(url, new Blob([JSON.stringify({})], { type: "application/json" }));
      }
    };

    window.addEventListener("beforeunload", abandonSession);
    return () => {
      window.removeEventListener("beforeunload", abandonSession);
      abandonSession();
    };
  }, []);

  const endDotMovement = () => {
    setShowDotMovement(false);
    setIsModalVisible(true);
  };

  const { exitSession, setExitSession } = useContext(DotContext);

  useEffect(() => {
    if (exitSession) {
      setIsModalVisible(false);
      setShowDotMovement(false);
      setMoodModalVisible(true);
      setExitSession(false);
      setIsWarningVisible(false);
      setIsPaused(false);
    }
  }, [exitSession, setExitSession, setIsPaused]);

  const handleMoodSubmit = async (mood, note, type) => {
    const payload =
      type === "pre"
        ? {
            user_id: userId,
            mood_pre: mood,
            comment_pre: note,
            session_type: "Unguided",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(intakeData && {
              emotional_state: intakeData.emotionalState,
              time_preference: intakeData.timePreference,
              recommended_session_slug: intakeData.recommendedSlug,
              actual_session_slug: intakeData.actualSlug,
            }),
          }
        : {
            mood_post: mood,
            comment_post: note,
            background_color: bgColor,
            dot_color: dotColor,
            dot_size: dotSize,
            dot_speed: dotSpeed,
            sound_option: soundFileToOption(soundFile),
            volume: volume,
            session_cues_enabled: sessionCuesEnabled,
          };

    const apiURL =
      type === "pre" ? "/api/sessions" : `/api/sessions/${sessionId}`;
    const method = type === "pre" ? "POST" : "PATCH";

    try {
      const response = await axios({
        method,
        url: apiURL,
        data: payload,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (mood <= 3) {
        console.log("Warning mood");
        setIsWarningVisible(true);
        setIsModalVisible(false);
      } else {
        setIsModalVisible(true);
      }

      if (type === "pre") {
        setSessionId(response.data.session_id);
        setMoodModalVisible(false);
        setPreSessionMood(mood);
        trackEvent("session_started", {
          session_type: "Unguided",
          mood_pre: mood,
          ...(intakeData && {
            emotional_state: intakeData.emotionalState,
            time_preference: intakeData.timePreference,
            was_recommended: intakeData.recommendedSlug === intakeData.actualSlug,
          }),
        });
      } else if (type === "post") {
        sessionIdRef.current = null;
        setSidebarVisible(false);
        trackEvent("session_completed", {
          session_type: "Unguided",
          mood_pre: preSessionMood,
          mood_post: mood,
          mood_delta: mood - preSessionMood,
        });
        navigate("/session-end");
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.code === "free_limit_reached") {
        toast.info(
          error.response.data.message ||
            "You've used all your free sessions this week. Subscribe for unlimited access."
        );
        navigate("/subscribe", { state: { from: "/sessions/unguided" } });
        return;
      }
      console.error("Error submitting mood:", error);
    }
  };

  const toggleNav = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const togglePause = () => {
    if (!isPaused) {
      trackEvent("session_paused", { session_type: "Unguided" });
    }
    setIsPaused(!isPaused);
  };

  const handleUserChoice = (continueSession) => {
    if (continueSession) {
      setShowDotMovement(true);
      setIsModalVisible(false);
    } else {
      setIsModalVisible(false);
      setMoodModalVisible(true);
    }
  };

  const handleRejectWarning = (rejectWarning) => {
    if (rejectWarning) {
      setShowDotMovement(true);
      setIsWarningVisible(false);
    } else {
      setIsWarningVisible(false);
      navigate("/emergency-support");
    }
  };

  const renderModalContent = () => (
    <div className="fixed inset-0 z-40 bg-session-bg sm:bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4">
      <div className="w-full sm:max-w-md bg-session-bg-elevated text-session-text sm:rounded-2xl sm:shadow-xl flex flex-col max-h-screen">
        <div className="flex-1 px-6 pt-10 pb-6 sm:py-8 overflow-y-auto">
          <p className="text-lg text-session-text leading-relaxed">Do you want to continue?</p>
        </div>
        <div className="px-6 pb-6 sm:pb-8 sticky bottom-0 bg-session-bg-elevated border-t border-session-text-secondary/10 sm:border-0 pt-4 flex flex-col gap-2">
          <button
            onClick={() => handleUserChoice(true)}
            className="w-full bg-session-accent text-session-bg font-semibold py-4 rounded-xl hover:opacity-90 transition"
          >
            Yes
          </button>
          <button
            onClick={() => handleUserChoice(false)}
            className="w-full bg-session-bg text-session-text border border-session-text-secondary/20 font-semibold py-4 rounded-xl hover:bg-session-bg/70 transition"
          >
            No
          </button>
        </div>
      </div>
    </div>
  );

  const renderWarningContent = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <p className="text-text mb-4">THIS IS THE WARNING MODAL?</p>
        <div className="flex gap-2">
          <button
            onClick={() => handleUserChoice(true)}
            className="bg-accent text-background font-semibold py-2 px-4 rounded hover:bg-primary transition duration-200"
          >
            Yes
          </button>
          <button
            onClick={() => handleUserChoice(false)}
            className="bg-accent text-background font-semibold py-2 px-4 rounded hover:bg-primary transition duration-200"
          >
            No
          </button>
        </div>
      </div>
    </div>
  );

  const handleReadinessComplete = () => {
    setShowReadinessCheck(false);
    setMoodModalVisible(true);
  };

  const handleReadinessCancel = () => {
    navigate("/sessions");
  };

  if (!isValidated || !user) {
    return <LoadingSkeleton />;
  }

  return (
    <SessionThemeWrapper className="relative flex flex-col items-center justify-center h-screen bg-session-bg text-session-text">
      <DynamicTitle>Unguided Session</DynamicTitle>
      {!sidebarVisible && !showReadinessCheck && !moodModalVisible && !isModalVisible && (
        <button
          onClick={toggleNav}
          className="absolute top-4 landscape:top-1 left-4 landscape:left-2 text-xl landscape:text-lg text-text z-50 focus:outline-none"
        >
          <span className="hamburger" style={{ color: dotColor }}>
            &#9776;
          </span>
        </button>
      )}

      {showReadinessCheck && (
        <ReadinessCheck
          onContinue={handleReadinessComplete}
          onCancel={handleReadinessCancel}
        />
      )}

      {moodModalVisible && (
        <MoodTracker
          onSubmit={(mood, note) =>
            handleMoodSubmit(mood, note, preSessionMood ? "post" : "pre")
          }
          isPreSession={!preSessionMood}
        />
      )}

      {isModalVisible && renderModalContent()}

      {isWarningVisible && (
        <CrisisModal
          open={isWarningVisible}
          onClose={() => setIsWarningVisible(false)}
          handleRejectWarning={handleRejectWarning}
          isInCrisis={preSessionMood === 1}
        >
          {renderWarningContent()}
        </CrisisModal>
      )}

      {showDotMovement && (
        <div className="w-full h-full flex items-center justify-center relative">
          <button
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-session-accent text-session-bg py-2 landscape:py-1 px-6 landscape:px-4 rounded hover:opacity-90 transition duration-200 z-50 font-bold landscape:text-sm pause-button-safe"
            onClick={togglePause}
          >
            {isPaused ? "Resume" : "Pause"}
          </button>
          <DotMovement
            shouldRun={showDotMovement}
            isPaused={isPaused}
            bgColor={bgColor}
            dotColor={dotColor}
            dotGlow={dotGlow}
            dotBrightness={dotBrightness}
            dotSize={dotSize}
            dotSpeed={dotSpeed}
            onCompletion={endDotMovement}
            soundFile={soundFile}
            mute={mute}
            volume={volume}
          />
        </div>
      )}
    </SessionThemeWrapper>
  );
}

export default UnguidedSession;
